<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import type { CreateWorkspaceRequest } from '@/types/workspace'

import WorkspaceForm from '@/components/workspaces/WorkspaceForm.vue'
import { useAppToast } from '@/composables/useAppToast'
import { createWorkspace } from '@/services/workspaceService'

definePage({
  meta: {
    requiresAuth: true,
    requiresInternalAccess: true,
  },
})

const router = useRouter()
const toast = useAppToast()
const { fetchUserWorkspaces } = useUser()
const { t } = useI18n()

// State management
const loading = ref(false)
const error = ref<string | null>(null)

/**
 * Creates a new workspace using the service.
 * @param requestData - The data for the new workspace.
 */
async function addWorkspace(requestData: CreateWorkspaceRequest) {
  loading.value = true
  error.value = null
  try {
    const newWorkspace = await createWorkspace(requestData)
    return newWorkspace
  } catch (err) {
    console.error('Error creating workspace:', err)
    error.value = t('pages.workspaces.messages.createError')
    return null
  } finally {
    loading.value = false
  }
}

async function handleCreateWorkspace(formData: CreateWorkspaceRequest) {
  const newWs = await addWorkspace(formData)
  if (newWs) {
    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('pages.workspaces.messages.createSuccess'),
      duration: 3000,
    })
    await fetchUserWorkspaces(true)

    router.push(`/workspaces/${newWs.id}`)
  } else {
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: error.value || t('pages.workspaces.messages.createError'),
      duration: 5000,
    })
  }
}

function handleCancel() {
  router.push('/workspaces') // Navigate back to the list page
}
</script>

<template>
  <div class="content-wrap">
    <h1 class="mb-4 text-2xl font-bold">
      {{ $t('pages.workspaces.create.title') }}
    </h1>

    <div class="mycard p-4 md:p-6">
      <WorkspaceForm :loading="loading" @submit="handleCreateWorkspace" @cancel="handleCancel" />
      <div v-if="error" class="mt-4 text-red-700 dark:text-red-400">
        {{ $t('common.messages.errorGeneral') }}: {{ error }}
      </div>
    </div>
  </div>
</template>
